// Documents.jsx
import { useState, useCallback } from "react";
import { Upload } from "antd";
import { Download, Edit, Trash, UploadCloudIcon, FileText, FileArchive } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import { ORGANIZATION_DOCUMENTS } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";

const Documents = () => {
    const { create, update } = useApiMethods();
    const [documentData, setDocumentData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const { add: addPermission, draft: draftPermission, edit: editPermission } = checkUserPermissionFunc("administrator", "Organization Documents", "SubMenu") || {};

    const [formData, setFormData] = useState({
        code: "",
        document_name: "",
        comments: "",
        is_active: true,
        uploaded_documents: [],
        doc_name: "",
        document_url: "",
    });

    // Reset form and modal state
    const resetForm = useCallback(() => {
        setFormData({
            code: "",
            document_name: "",
            comments: "",
            is_active: true,
            uploaded_documents: [],
            doc_name: "",
            document_url: "",
        });
        setIsEditMode(false);
        setEditingId(null);
        setIsDialogOpen(false);
        setSelectedDocument(null);
    }, []);

    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleStatusToggle = useCallback(() => {
        setFormData(prev => ({
            ...prev,
            is_active: !prev.is_active
        }));
    }, []);

    const validateFile = (file) => {
        const MAX_FILE_SIZE_MB = 1;
        const fileSizeMB = file.size / (1024 * 1024);
        const isWordDocument = file.type === 'application/msword' ||
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        if (!isWordDocument) {
            ToastMsg("error", "Only Word documents (.doc, .docx) are supported");
            return false;
        }

        if (fileSizeMB > MAX_FILE_SIZE_MB) {
            ToastMsg("error", `File size must be less than ${MAX_FILE_SIZE_MB}MB`);
            return false;
        }

        return true;
    };

    const handleBeforeUpload = (file) => {
        if (!validateFile(file)) return false;

        setFormData(prev => ({
            ...prev,
            doc_name: file.name,
            document_url: "" // Clear existing URL when new file is selected
        }));
        return true;
    };

    const handleUploadChange = ({ fileList }) => {
        setFormData(prev => ({
            ...prev,
            uploaded_documents: Array.isArray(fileList) ? fileList : [],
            document_url: "" // Clear URL when new file is selected
        }));
    };

    const handleRemoveFile = () => {
        setFormData(prev => ({
            ...prev,
            doc_name: "",
            uploaded_documents: [],
            document_url: ""
        }));
    };

    const handleRemoveExistingDocument = () => {
        setFormData(prev => ({
            ...prev,
            document_url: "",
            doc_name: ""
        }));
    };

    const handleEditDocument = (document) => {
        setIsEditMode(true);
        setEditingId(document?.code);
        setFormData({
            id: document?.id || null,
            code: document?.code || "",
            document_name: document?.document_name || "",
            comments: document?.comments || "",
            is_active: document?.is_active ?? true,
            uploaded_documents: [],
            doc_name: document?.document_name || "",
            document_url: document?.document_url || "",
        });
        setIsDialogOpen(true);
    };

    const handleAddDocument = () => {
        resetForm();
        setIsEditMode(false);
        setIsDialogOpen(true);
    };

    const handleOpenDeleteConfirm = (document) => {
        setSelectedDocument(document);
        setIsDeleteConfirmOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
        setIsDeleteConfirmOpen(false);
        setSelectedDocument(null);
    };

    const getFileNameFromUrl = (url) => {
        if (!url) return "";
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            return pathname.split('/').pop() || "document";
        } catch {
            return url.split('/').pop() || "document";
        }
    };

    const documentsColumns = [
        {
            accessor: "code",
            Header: "Document Code",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "document_name",
            Header: "Document Name",
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "comments",
            Header: "Comments",
            Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description")
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Actions",
                sortable: false,
                Cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        {/* Edit Button */}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                            onClick={() => handleEditDocument(row)}
                            aria-label={`Edit document ${row.document_name}`}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>

                        {/* Download Button */}
                        <a
                            href={row.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Download ${row.document_name}`}
                        >
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30"
                            >
                                <Download className="h-4 w-4" />
                            </Button>
                        </a>

                        {/* Delete Button (only for draft documents) */}
                        {row.is_draft === true && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                                onClick={() => handleOpenDeleteConfirm(row)}
                                aria-label={`Delete document ${row.document_name}`}
                            >
                                <Trash className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                ),
            },
        ] : [])
    ];

    const handleConfirmDelete = async () => {
        if (!selectedDocument) return;

        try {
            setIsDeleteLoading(true);

            // Prepare data for deletion
            const dataForm = new FormData();
            dataForm.append("code", selectedDocument.code || "");
            dataForm.append("document_name", selectedDocument.document_name || "");
            dataForm.append("comments", selectedDocument.comments || "");
            dataForm.append("id", selectedDocument.id || "");
            dataForm.append("is_active", selectedDocument.is_active);
            dataForm.append("is_draft", true);
            dataForm.append("draft_delete", true);

            const res = await update(ORGANIZATION_DOCUMENTS, dataForm, true);

            if (res.status === 'success') {
                ToastMsg("success", "Document deleted successfully");
                handleCloseDeleteConfirm();
                setServerTrigger(prev => !prev); // Refresh table data
            } else {
                ToastMsg("error", res.message || "Failed to delete document");
            }
        } catch (error) {
            console.error("Delete error:", error);
            ToastMsg("error", "Failed to delete document");
        } finally {
            setIsDeleteLoading(false);
        }
    };

    const handleSave = async (isDraft = false) => {
        // Validation for new documents without file
        if (!isEditMode && editingId && !formData.uploaded_documents.length && !formData.document_url) {
            ToastMsg("error", "Please upload a document");
            return;
        }

        // Validation for editing when document is removed
        if (isEditMode && !formData.document_url && !formData.uploaded_documents.length) {
            ToastMsg("error", "Cannot update record without a document. Please add a new document or cancel.");
            return;
        }

        // Prepare FormData
        const dataForm = new FormData();
        dataForm.append("code", formData.code || "");
        dataForm.append("document_name", formData.document_name || "");
        dataForm.append("comments", formData.comments || "");
        dataForm.append("id", formData.id || "");
        dataForm.append("is_active", formData.is_active);
        dataForm.append("is_draft", isDraft);

        // Handle file upload
        if (formData.uploaded_documents.length > 0 && formData.uploaded_documents[0]?.originFileObj) {
            dataForm.append("uploaded_documents", formData.uploaded_documents[0].originFileObj);
        } else if (formData.document_url) {
            dataForm.append("document_url", formData.document_url);
        }

        try {
            setIsLoading(true);
            const res = isEditMode
                ? await update(ORGANIZATION_DOCUMENTS, dataForm, true)
                : await create(ORGANIZATION_DOCUMENTS, dataForm, true);

            if (res.status === 'success') {
                ToastMsg("success", res.message);
                resetForm();
                setServerTrigger(prev => !prev);
            }
        } catch (error) {
            console.error("Save error:", error);
            ToastMsg("error", `Failed to ${isEditMode ? 'update' : 'create'} document`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ContainerCard>
            {/* Header */}
            <ModuleTitleCard Icon={FileArchive} title={'Organization Documents'} subtitle="Store and manage important organizational documents" totalRecords={totalRecords} buttonEnable={!!addPermission} setOpenDialog={handleAddDocument} buttonName={'Add Document'} />

            {/* Documents Table */}
            <Reactable
                columns={documentsColumns}
                data={documentData}
                setData={setDocumentData}
                setTotalCount={setTotalRecords}
                serverTrigger={serverTrigger}
                url={ORGANIZATION_DOCUMENTS}
                serverSide={true}
                tableHeight="calc(100vh - 320px)"
                rowSize={editPermission ? "sm" : null}
                isDraftRequired={!!draftPermission}
                isCSVRequired={true}
                isPDFRequired={true}
            />

            {/* Add/Edit Document Modal */}
            <DialogModal
                open={isDialogOpen}
                onClose={resetForm}
                onDraft={() => handleSave(true)}
                onSave={() => handleSave(false)}
                title={isEditMode ? "Edit Document" : "Add Document"}
                closeLabel="Cancel"
                saveLabel={isEditMode ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={isEditMode}
                loading={isLoading}
                width="700px"
            >
                <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                    <div className={cn("grid gap-8 grid-cols-1 md:grid-cols-2", "max-h-90 overflow-y-auto pr-2")}>
                        {/* Left Column - Form Fields */}
                        <div className="space-y-4">
                            {/* Document Name */}
                            <div className="space-y-2">
                                <label
                                    htmlFor="documentName"
                                    className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1"
                                >
                                    Document Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    id="documentName"
                                    value={formData.document_name}
                                    onChange={(e) => handleInputChange("document_name", e.target.value)}
                                    placeholder="Enter document name"
                                    required
                                    disabled={isLoading}
                                    className={cn(
                                        "w-full rounded-lg transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                />
                            </div>

                            {/* Comments */}
                            <div className="space-y-2">
                                <label
                                    htmlFor="comments"
                                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Comments
                                </label>
                                <Textarea
                                    id="comments"
                                    value={formData.comments}
                                    onChange={(e) => handleInputChange("comments", e.target.value)}
                                    placeholder="Enter document description"
                                    rows={3}
                                    showCharCount={true}
                                    maxLength={250}
                                    disabled={isLoading}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                    )}
                                />
                            </div>

                            {/* Active Status Toggle */}
                            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Active Status
                                </label>
                                <div className="flex items-center gap-3">
                                    <span className={cn(
                                        "text-sm font-medium",
                                        formData.is_active
                                            ? "text-green-600 dark:text-green-400"
                                            : "text-gray-500 dark:text-gray-400"
                                    )}>
                                        {formData.is_active ? "Active" : "Inactive"}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleStatusToggle}
                                        disabled={isLoading || !isEditMode}
                                        className={cn(
                                            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200",
                                            formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                            isLoading || !isEditMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                        )}
                                        aria-label={formData.is_active ? "Deactivate document" : "Activate document"}
                                    >
                                        <span
                                            className={cn(
                                                "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200",
                                                formData.is_active ? "translate-x-6" : "translate-x-1"
                                            )}
                                        />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - File Upload */}
                        <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Document Upload {!isEditMode && <span className="text-red-500">*</span>}
                            </label>
                            {/* Show existing document when editing */}
                            <div>
                                {isEditMode && formData.document_url ? (
                                    <div className={cn(
                                        "border border-blue-200 dark:border-blue-800",
                                        "rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20",
                                        "flex items-center justify-between"
                                    )}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-lg flex items-center justify-center">
                                                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
                                                    {getFileNameFromUrl(formData.document_url)}
                                                </p>
                                                <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                                    Current document
                                                </p>
                                                <a
                                                    href={formData.document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 underline"
                                                >
                                                    View document
                                                </a>
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleRemoveExistingDocument}
                                            disabled={isLoading}
                                            className="text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                                        >
                                            <Trash className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : !formData.doc_name ? (
                                    <Upload
                                        accept=".doc,.docx"
                                        beforeUpload={handleBeforeUpload}
                                        onChange={handleUploadChange}
                                        fileList={[]}
                                        maxCount={1}
                                        showUploadList={false}
                                        disabled={isLoading}
                                        style={{ width: '100%' }}
                                    >
                                        <div className={cn(
                                            "border-2 border-dashed border-gray-300 dark:border-gray-600",
                                            "rounded-lg p-3 text-center cursor-pointer w-full",
                                            "hover:border-sidebar-primary transition-all duration-200",
                                            "hover:bg-gray-50 dark:hover:bg-gray-800/50",
                                            isLoading && "opacity-50 cursor-not-allowed"
                                        )}>
                                            <UploadCloudIcon className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                Click to upload document
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-500">
                                                Supported formats: .doc, .docx (Max: 1MB)
                                            </p>
                                        </div>
                                    </Upload>
                                ) : (
                                    <div className={cn(
                                        "border border-green-200 dark:border-green-800",
                                        "rounded-lg p-4 bg-green-50 dark:bg-green-900/20",
                                        "flex items-center justify-between"
                                    )}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-lg flex items-center justify-center">
                                                <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
                                                    {formData.doc_name}
                                                </p>
                                                <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                                    Ready to upload
                                                </p>
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleRemoveFile}
                                            disabled={isLoading}
                                            className="text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                                        >
                                            <Trash className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {isEditMode
                                        ? "Upload a new file to replace the current document."
                                        : "Only one document can be uploaded. Supported formats: Microsoft Word documents."
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                </form>
            </DialogModal>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={handleCloseDeleteConfirm}
                onConfirm={handleConfirmDelete}
                title="Delete Document"
                description={`Are you sure you want to delete "${selectedDocument?.document_name}"? This action cannot be undone.`}
                type="warning"
                confirmlabel="Delete"
                cancellabel="Cancel"
                destructive={true}
                loading={isDeleteLoading}
            />
        </ContainerCard>
    );
};

export default Documents;